import { Navigate } from 'react-router-dom';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { OPS_LANDING_PATH, visibleNavGroups } from '../layout/navigation.ts';
import { PageHeader } from '../ui/index.ts';

/**
 * `/home` — idle authenticated default (router#2260). Login used to
 * dump every ops-admin onto the morning cockpit, which fires the PnL
 * summary whether the operator wanted that page or not. This page is
 * the opposite: identity from the in-memory session mirror, one line
 * of guidance, zero queries. Role-less operators belong on `/landing`
 * (#854) and are sent there if they open this path.
 */
export function OpsHomePage(): JSX.Element {
  const { user, status } = useSession();
  const t = useT();
  // RequireAuth already gates this route; the loading check is so a
  // role-bearing session is not sent to /landing while /me is in flight.
  if (status === 'loading') {
    return <section className="flex max-w-xl flex-col gap-4 py-8" data-testid="ops-home-page" />;
  }
  if (status !== 'authenticated' || visibleNavGroups(user?.roles ?? []).length === 0) {
    return <Navigate to={OPS_LANDING_PATH} replace />;
  }
  const displayIdentity =
    user?.displayName !== undefined && user.displayName.length > 0
      ? user.displayName
      : user?.email !== undefined && user.email.length > 0
        ? user.email
        : t('shell.signed_in_fallback');

  return (
    <section className="flex max-w-xl flex-col gap-4 py-8" data-testid="ops-home-page">
      <PageHeader title={t('ops_home.title')} />
      <p className="m-0 text-body text-paper-mid">{t('ops_home.body')}</p>
      <p className="m-0 text-body text-paper" data-testid="ops-home-identity">
        {displayIdentity}
      </p>
    </section>
  );
}
